
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext.js";
import { useAuth } from "../../../contexts/AuthContext.js";
import { Star, ShoppingCart, Heart, Check, AlertCircle } from "lucide-react";
import { useFavorites } from "../../../contexts/FavoritesContext.js";
import { showCartAdd, showWishlistAdd, showWishlistRemove, showSuccess, showError, showWarning } from "../../../utils/toastUtils.js";
import api from "../../../api/client.js";
import logger from "../../../utils/logger.js";
import Breadcrumb from "../../../components/ui/Breadcrumb/Breadcrumb";
import LoadingScreen from "../../../components/ui/Loading/Loading";
import "./ProductDetail.css";

// Star Rating Display
function StarRatingDisplay({ value = 0, showValue = false }) {
  const rating = Math.max(0, Math.min(5, Number(value) || 0));

  return (
    <div className="star-rating-detail">
      <div className="stars">
        <div className="stars-bg">★★★★★</div>
        <div className="stars-fill" style={{ width: `${(rating / 5) * 100}%` }}>
          ★★★★★
        </div>
      </div>
      {showValue && <span className="rating-text">{rating.toFixed(1)}</span>}
    </div>
  );
}

// Interactive Star Rating
function InteractiveStarRating({ value, onChange }) {
  const [hoverValue, setHoverValue] = useState(0);

  return (
    <div className="interactive-stars">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`star-btn ${star <= (hoverValue || value) ? 'active' : ''}`}
          onClick={() => onChange(star)}
          onMouseEnter={() => setHoverValue(star)}
          onMouseLeave={() => setHoverValue(0)}
        >
          <Star
            size={28}
            fill={star <= (hoverValue || value) ? 'var(--orange-500)' : 'none'}
            stroke={star <= (hoverValue || value) ? 'var(--orange-500)' : 'var(--border-color)'}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const { isLogged } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();

  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewNotice, setReviewNotice] = useState("");
  const [reviewError, setReviewError] = useState("");
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [error, setError] = useState("");

  const isAr = lang === "ar";

  async function loadProductDetails(productId, mountedRef = { current: true }) {
    const data = await api.apiGet(`products/${productId}`);

    if (!mountedRef.current) return;

    if (!data?.product) {
      setError(isAr ? "المنتج غير موجود" : "Product not found");
      setLoading(false);
      return;
    }

    const p = data.product;
    setProduct(p);

    if (Array.isArray(p.variants)) {
      setVariants(p.variants);
      if (p.variants.length > 0) {
        setSelectedVariantId((current) =>
          current && p.variants.some((v) => v.id === current)
            ? current
            : p.variants[0].id
        );
      }
    }

    setLoading(false);
  }

  useEffect(() => {
    if (!id) return;

    const mountedRef = { current: true };

    async function fetchData() {
      try {
        setLoading(true);
        setError("");
        await loadProductDetails(id, mountedRef);
      } catch (err) {
        if (!mountedRef.current) return;
        logger.error(err);
        setError(isAr ? "خطأ أثناء تحميل المنتج" : "Error loading product");
        setLoading(false);
      }
    }

    fetchData();

    return () => {
      mountedRef.current = false;
    };
  }, [id, lang, isAr]);

  const selectedVariant = useMemo(() => {
    if (!selectedVariantId) return null;
    return variants.find((v) => v.id === selectedVariantId) || null;
  }, [selectedVariantId, variants]);

  const imageSrc =
    selectedVariant?.image_url ||
    product?.image_url ||
    (product?.image && /^https?:\/\//i.test(product.image) ? product.image : null);

  const basePrice = useMemo(() => {
    if (!product) return 0;
    const base = product.sale_price && product.sale_price > 0
      ? product.sale_price
      : product.price || 0;
    if (selectedVariant?.price) return selectedVariant.price;
    return base;
  }, [product, selectedVariant]);

  const originalPrice = useMemo(() => {
    if (product?.sale_price && product.sale_price > 0 && product.price > product.sale_price) {
      return product.price;
    }
    return null;
  }, [product]);

  const baseStock = selectedVariant?.stock ?? product?.stock ?? 0;
  const isOutOfStock = baseStock <= 0;
  const isLiked = isFavorite(product?.id);

  async function handleAddToCart() {
    if (!isLogged) {
      navigate("/login");
      return;
    }

    if (!selectedVariantId) {
      showWarning(isAr ? "اختر خيار أولاً" : "Select an option first");
      return;
    }

    try {
      setAdding(true);
      const res = await api.apiPost("cart/add", {
        variant_id: Number(selectedVariantId),
        quantity: Number(quantity) || 1,
      });

      if (res?.message === "Added to cart" || res?.success) {
        showCartAdd(product?.name_en || "Product");
      } else {
        showError(isAr ? "خطأ" : "Error", res?.message || "Error");
      }
    } catch (err) {
      logger.error(err);
      showError(
        isAr ? "تعذر الإضافة للسلة" : "Unable to add to cart",
        err?.response?.data?.message || err?.message || (isAr ? "هذا المنتج غير متاح للشراء حاليًا." : "This product is not available for purchase right now.")
      );
    } finally {
      setAdding(false);
    }
  }

  const handleToggleFavorite = async () => {
    if (!isLogged) {
      navigate("/login");
      return;
    }
    if (!product) return;

    try {
      const didUpdate = await toggleFavorite({
        id: product.id,
        name: isAr ? (product.name_ar || product.name_en) : product.name_en,
        price: basePrice,
        image: imageSrc,
      });

      if (didUpdate) {
        isLiked ? showWishlistRemove(product.name_en) : showWishlistAdd(product.name_en);
      }
    } catch (error) {
      logger.error("Favorite error:", error);
      showError(isAr ? "خطأ" : "Error", isAr ? "حدث خطأ" : "Error occurred");
    }
  };

  function handleReviewClick() {
    if (!isLogged) {
      navigate("/login");
      return;
    }

    if (!product?.can_review) {
      showWarning(
        isAr ? "تنبيه" : "Notice",
        isAr ? "يمكنك التقييم بعد شراء المنتج" : "Review available after purchase"
      );
      return;
    }

    setReviewNotice("");
    setReviewError("");
    setReviewOpen((c) => !c);
  }

  async function handleReviewSubmit(event) {
    event.preventDefault();
    if (!isLogged) {
      navigate("/login");
      return;
    }
    if (!product?.can_review) {
      showWarning(isAr ? "تنبيه" : "Notice", isAr ? "التقييم بعد الشراء" : "Review after purchase");
      return;
    }

    try {
      setReviewSubmitting(true);
      const response = await api.apiPost(`products/${id}/reviews`, {
        rating: Number(reviewForm.rating) || 0,
        comment: reviewForm.comment.trim(),
      });

      setReviewNotice(response?.message || (isAr ? "تم إرسال التقييم" : "Review submitted"));
      setReviewOpen(false);
      setReviewForm({ rating: 5, comment: "" });
      setLoading(true);
      await loadProductDetails(id);
      showSuccess(isAr ? "شكراً!" : "Thanks!", isAr ? "تم إرسال تقييمك" : "Your review submitted");
    } catch (submitError) {
      logger.error(submitError);
      setReviewError(submitError?.message || (isAr ? "تعذر الإرسال" : "Failed to submit"));
      showError(isAr ? "خطأ" : "Error", isAr ? "تعذر إرسال التقييم" : "Failed to submit review");
    } finally {
      setReviewSubmitting(false);
    }
  }

  const breadcrumbItems = useMemo(() => {
    const items = [];
    items.push({
      label: isAr ? "المتجر" : "Shop",
      link: "/shop",
    });

    if (product) {
      const productName = isAr
        ? (product.name_ar || product.name_en)
        : (product.name_en || product.name_ar);
      items.push({ label: productName });
    } else {
      items.push({ label: isAr ? "تفاصيل المنتج" : "Product Detail" });
    }

    return items;
  }, [isAr, product]);

  if (loading) {
    return (
      <div className="product-detail" dir={isAr ? "rtl" : "ltr"}>
        <div className="page-breadcrumb">
          <div className="breadcrumb-container">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>
        <LoadingScreen lang={lang} minHeight="50vh" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail" dir={isAr ? "rtl" : "ltr"}>
        <div className="page-breadcrumb">
          <div className="breadcrumb-container">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>
        <div className="container py-4">
          <div className="detail-error">
            <AlertCircle size={40} style={{ color: "#dc2626" }} />
            <p className="text-danger">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const title = isAr ? product.name_ar || product.name_en : product.name_en || product.name_ar;
  const description = isAr ? product.description_ar || product.description : product.description;
  const averageRating = Number(product?.average_rating || 0);
  const reviewsCount = Number(product?.reviews_count || 0);
  const canReview = Boolean(product?.can_review);

  return (
    <div className="product-detail" dir={isAr ? "rtl" : "ltr"}>
      <div className="page-breadcrumb">
        <div className="breadcrumb-container">
          <Breadcrumb items={breadcrumbItems} />
        </div>
      </div>

      <div className="container py-4">
        <div className="row g-4">
          <div className="col-lg-5">
            <div className="detail-image-wrap">
              {imageSrc ? (
                <img src={imageSrc} alt={title} className="detail-image" loading="lazy" />
              ) : (
                <div className="detail-no-image">{isAr ? "لا توجد صورة" : "No image"}</div>
              )}
              <button className={`detail-heart-btn ${isLiked ? 'liked' : ''}`} onClick={handleToggleFavorite}>
                <Heart size={20} fill={isLiked ? 'currentColor' : 'none'} />
              </button>
            </div>
          </div>

          <div className="col-lg-7">
            <div className="detail-info">
              <h1 className="detail-title">{title}</h1>

              <div className="detail-rating">
                <StarRatingDisplay value={averageRating} showValue />
                <span className="detail-reviews">({reviewsCount} {isAr ? 'تقييم' : 'reviews'})</span>
              </div>

              <div className="detail-price">
                <span className="detail-current">${basePrice.toFixed(2)}</span>
                {originalPrice && <span className="detail-old">${originalPrice.toFixed(2)}</span>}
              </div>

              {description && <p className="detail-desc">{description}</p>}

              <div className={`detail-stock ${isOutOfStock ? 'out' : 'in'}`}>
                {isOutOfStock ? (
                  <><AlertCircle size={16} /> {isAr ? 'غير متوفر' : 'Out of stock'}</>
                ) : (
                  <><Check size={16} /> {isAr ? 'متوفر' : 'In stock'} ({baseStock})</>
                )}
              </div>

              {variants.length > 0 && (
                <div className="detail-variants">
                  <label>{isAr ? 'الخيارات:' : 'Options:'}</label>
                  <div className="variant-list">
                    {variants.map((v) => (
                      <button
                        key={v.id}
                        className={`variant-item ${selectedVariantId === v.id ? 'active' : ''} ${v.stock <= 0 ? 'disabled' : ''}`}
                        onClick={() => v.stock > 0 && setSelectedVariantId(v.id)}
                        disabled={v.stock <= 0}
                      >
                        {v.sku}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="detail-quantity">
                <label>{isAr ? 'الكمية:' : 'Quantity:'}</label>
                <div className="qty-control">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>-</button>
                  <input type="number" min="1" max={baseStock} value={quantity} onChange={(e) => setQuantity(Math.max(1, Math.min(baseStock, Number(e.target.value) || 1)))} />
                  <button onClick={() => setQuantity(Math.min(baseStock, quantity + 1))} disabled={quantity >= baseStock}>+</button>
                </div>
              </div>

              <div className="detail-actions">
                <button className="btn-add" onClick={handleAddToCart} disabled={isOutOfStock || adding}>
                  <ShoppingCart size={18} />
                  {adding ? (isAr ? "جارٍ..." : "Adding...") : (isAr ? "أضف للسلة" : "Add to cart")}
                </button>
                <button className={`btn-fav ${isLiked ? 'liked' : ''}`} onClick={handleToggleFavorite}>
                  <Heart size={18} fill={isLiked ? 'currentColor' : 'none'} />
                  {isLiked ? (isAr ? 'محفوظ' : 'Saved') : (isAr ? 'حفظ' : 'Save')}
                </button>
              </div>

              <div className="detail-reviews-section">
                <div className="reviews-header">
                  <h4>{isAr ? 'التقييمات' : 'Reviews'}</h4>
                  {canReview ? (
                    <button className="btn-toggle-review" onClick={handleReviewClick}>
                      {reviewOpen ? (isAr ? "إغلاق" : "Close") : (isAr ? "أضف تقييم" : "Add Review")}
                    </button>
                  ) : (
                    <span className="review-hint">{isAr ? "التقييم بعد الشراء" : "Review after purchase"}</span>
                  )}
                </div>

                {reviewOpen && canReview && (
                  <form onSubmit={handleReviewSubmit} className="review-form">
                    <div className="form-group">
                      <label>{isAr ? 'تقييمك:' : 'Your rating:'}</label>
                      <InteractiveStarRating value={reviewForm.rating} onChange={(r) => setReviewForm((p) => ({ ...p, rating: r }))} />
                    </div>
                    <div className="form-group">
                      <label>{isAr ? 'تعليقك:' : 'Your comment:'}</label>
                      <textarea rows="3" value={reviewForm.comment} onChange={(e) => setReviewForm((p) => ({ ...p, comment: e.target.value }))} placeholder={isAr ? "اكتب تعليقك..." : "Write your comment..."} />
                    </div>
                    <div className="form-btns">
                      <button type="submit" className="btn-submit" disabled={reviewSubmitting}>
                        {reviewSubmitting ? (isAr ? "جارٍ..." : "Submitting...") : (isAr ? "إرسال" : "Submit")}
                      </button>
                      <button type="button" className="btn-cancel" onClick={() => setReviewOpen(false)} disabled={reviewSubmitting}>
                        {isAr ? "إلغاء" : "Cancel"}
                      </button>
                    </div>
                  </form>
                )}

                {reviewNotice && <div className="alert success">{reviewNotice}</div>}
                {reviewError && <div className="alert error">{reviewError}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
